import { GithubLogo, GoogleLogo } from '@/components/ui/icons';
import { useState, type ReactNode } from 'react';
import { OAUTH_PROVIDER_LABELS, type OAuthProviderId, type OAuthStartInput } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { authApi } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/stores/auth';

export const PROVIDER_ICONS: Record<OAuthProviderId, ReactNode> = {
  google: <GoogleLogo size={18} />,
  github: <GithubLogo size={18} />,
};

/** Takes the browser to the provider (the server keeps the sign-in's state in a short-lived cookie). */
export async function goToProvider(provider: OAuthProviderId, input: OAuthStartInput) {
  const { url } = await authApi.oauthStart(provider, input);
  window.location.assign(url);
}

/**
 * "Continue with Google / GitHub" for the providers this server has
 * set up. Shows nothing when none are.
 */
export function OAuthButtons({
  mode,
  inviteCode,
  acceptTerms,
  next,
  disabledReason,
  onError,
}: {
  mode: 'login' | 'signup';
  inviteCode?: string;
  acceptTerms?: boolean;
  next?: string;
  /** Why the buttons can't be used yet (e.g. the terms aren't ticked); shown instead of acting. */
  disabledReason?: string | null;
  onError: (message: string) => void;
}) {
  const providers = useAuth((s) => s.config.oauthProviders);
  const [busy, setBusy] = useState<OAuthProviderId | null>(null);
  if (!providers.length) return null;

  const go = async (provider: OAuthProviderId) => {
    if (disabledReason) return onError(disabledReason);
    setBusy(provider);
    try {
      await goToProvider(provider, { mode, inviteCode: inviteCode?.trim() || undefined, acceptTerms, next });
    } catch (err) {
      onError(errorMessage(err));
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-3 text-xs font-semibold tracking-wide text-fg-muted uppercase" aria-hidden>
        <span className="h-px flex-1 bg-line" /> or <span className="h-px flex-1 bg-line" />
      </div>
      {providers.map((provider) => (
        <Button key={provider} type="button" variant="secondary" size="lg" block icon={PROVIDER_ICONS[provider]} loading={busy === provider} onClick={() => void go(provider)}>
          Continue with {OAUTH_PROVIDER_LABELS[provider]}
        </Button>
      ))}
    </div>
  );
}

/** What went wrong coming back from a provider, in words. */
export function oauthErrorMessage(code: string | null, provider: string | null): string | null {
  if (!code) return null;
  const name = provider && provider in OAUTH_PROVIDER_LABELS ? OAUTH_PROVIDER_LABELS[provider as OAuthProviderId] : 'that provider';
  switch (code) {
    case 'expired':
      return 'That sign-in took too long, or was started in another window. Please try again.';
    case 'cancelled':
      return `Signing in with ${name} was cancelled.`;
    case 'provider':
      return `We couldn't finish signing in with ${name}. Please try again in a moment.`;
    case 'not_linked':
      return `No account uses this ${name} account yet. Create an account with it, or log in with your password and connect ${name} in Settings → Security.`;
    case 'email_in_use':
      return `There's already an account with that email. Log in with your password, then connect ${name} in Settings → Security.`;
    case 'email_unverified':
      return `${name} hasn't confirmed your email address, so it can't be used to create an account.`;
    case 'invite':
      return 'Signing up needs a valid invite right now. Enter the code from your invite link, then try again.';
    case 'suspended':
      return 'This account has been suspended.';
    case 'linked_elsewhere':
      return `That ${name} account is already connected to a different account.`;
    case 'already_connected':
      return `You already have a different ${name} account connected. Disconnect it first.`;
    default:
      return 'Something went wrong signing in. Please try again.';
  }
}
